import { TUTORIAL_BASE_SUPPORTED_ACTIONS } from '@/feature/tutorialAgent/tutorialAgentTypes'
import type {
  TutorialElementRect,
  TutorialElementSnapshot,
  TutorialUiSnapshot,
  TutorialNavigationItem,
  TutorialCapability,
  TutorialActionHandlers,
  TutorialActionType,
  TutorialContainerSnapshot
} from '@/feature/tutorialAgent/tutorialAgentTypes'
import { getRecentRoutes } from '@/feature/tutorialAgent/tutorialHistory'
import {
  getDropdownItems,
  getSidebarItems,
  type NavItems
} from '@/components/customUi/Navbar/sidebarConfig'
import { useUserStore } from '@/stores/userStore'
import { useTutorialAgentStore } from '@/stores/tutorialAgentStore'
import { getTutorialAliasMetrics } from '@/feature/tutorialAgent/tutorialIdAliasStore'
import { getTutorialActionResolutionMetrics } from '@/feature/tutorialAgent/tutorialActionResolutionMetrics'
import { i18n } from '@/components/lang/langHandler'
import { router } from '@/router'

type TutorialRegistryEntry = {
  id: string
  label: string
  role?: string | null
  priority?: number | null
  state?: Record<string, string | number | boolean | null>
  element: HTMLElement
}

const registry = new Map<string, TutorialRegistryEntry>()

const slugify = (value: string) => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
}

const getSnapshotHandlers = (): TutorialActionHandlers => {
  try {
    const tutorialAgentStore = useTutorialAgentStore()
    return tutorialAgentStore.handlers ?? {}
  } catch {
    return {}
  }
}

const getSupportedActions = (handlers: TutorialActionHandlers): TutorialActionType[] => {
  const supported = new Set<TutorialActionType>(TUTORIAL_BASE_SUPPORTED_ACTIONS)
  if (handlers.openPanel) supported.add('openPanel')
  if (handlers.openInputSheet) supported.add('openInputSheet')
  if (handlers.closeInputSheet) supported.add('closeInputSheet')
  if (handlers.insertDemoFile) supported.add('insertDemoFile')
  if (handlers.hasEditorText) supported.add('waitForEditorText')
  if (handlers.hasGeneratedOutput) supported.add('waitForGeneratedOutput')
  if (handlers.setFormats) supported.add('setFormats')
  return Array.from(supported)
}

const uniqueCapabilities = (capabilities: TutorialCapability[]): TutorialCapability[] => {
  const seen = new Set<string>()
  const result: TutorialCapability[] = []
  capabilities.forEach(capability => {
    const key = `${capability.actionType}:${capability.targetId ?? ''}:${capability.route ?? ''}:${capability.intent}`
    if (seen.has(key)) return
    seen.add(key)
    result.push(capability)
  })
  return result
}

const buildHandlerCapabilities = (handlers: TutorialActionHandlers): TutorialCapability[] => {
  const capabilities: TutorialCapability[] = []

  if (handlers.openPanel) {
    capabilities.push({
      id: 'cap.handler.open_panel_input',
      intent: 'open_input_panel',
      label: 'Open input panel',
      actionType: 'openPanel',
      source: 'handler',
      priority: 3,
      meta: { panel: 'input' }
    })
    capabilities.push({
      id: 'cap.handler.open_panel_output',
      intent: 'open_output_panel',
      label: 'Open output panel',
      actionType: 'openPanel',
      source: 'handler',
      priority: 3,
      meta: { panel: 'output' }
    })
  }

  if (handlers.openInputSheet) {
    const sheets: Array<'upload' | 'library' | 'content' | 'scraper' | 'webSearch'> = [
      'upload',
      'library',
      'content',
      'scraper',
      'webSearch'
    ]
    sheets.forEach(sheet => {
      capabilities.push({
        id: `cap.handler.open_input_sheet_${sheet}`,
        intent: `open_${sheet}_sheet`,
        label: `Open ${sheet} sheet`,
        actionType: 'openInputSheet',
        source: 'handler',
        priority: 2,
        meta: { sheet }
      })
    })
  }

  if (handlers.closeInputSheet) {
    capabilities.push({
      id: 'cap.handler.close_input_sheet',
      intent: 'close_input_sheet',
      label: 'Close input sheet',
      actionType: 'closeInputSheet',
      source: 'handler',
      priority: 1
    })
  }

  if (handlers.insertDemoFile) {
    capabilities.push({
      id: 'cap.handler.insert_demo_file',
      intent: 'insert_demo_file',
      label: 'Insert demo file',
      actionType: 'insertDemoFile',
      source: 'handler',
      priority: 2
    })
  }

  if (handlers.hasEditorText) {
    capabilities.push({
      id: 'cap.handler.wait_for_editor_text',
      intent: 'wait_for_editor_text',
      label: 'Wait for editor text',
      actionType: 'waitForEditorText',
      source: 'handler',
      priority: 1
    })
  }

  if (handlers.hasGeneratedOutput) {
    capabilities.push({
      id: 'cap.handler.wait_for_generated_output',
      intent: 'wait_for_generated_output',
      label: 'Wait for generated output',
      actionType: 'waitForGeneratedOutput',
      source: 'handler',
      priority: 1
    })
  }

  if (handlers.setFormats) {
    capabilities.push({
      id: 'cap.handler.set_formats',
      intent: 'set_output_format',
      label: 'Set output format',
      actionType: 'setFormats',
      source: 'handler',
      priority: 2
    })
  }

  return capabilities
}

const translateNavLabel = (label: string) => {
  const key = label.trim()
  if (!key) return ''
  if (i18n.global.te(key)) {
    const translated = i18n.global.t(key)
    return typeof translated === 'string' ? translated : String(translated)
  }
  return key
}

const toNavItem = (item: NavItems, group: string): TutorialNavigationItem => ({
  id: `tutorial.nav.${item.label}`,
  label: translateNavLabel(String(item.label)),
  route: item.route ?? null,
  group
})

const flattenNavItems = (items: NavItems[], group: string): TutorialNavigationItem[] => {
  return items.flatMap(item => {
    const current = toNavItem(item, group)
    const subroutes = item.subroutes ? flattenNavItems(item.subroutes, `${group}.sub`) : []
    return [current, ...subroutes]
  })
}

const buildNavigationContext = (): {
  navbarVisible: boolean
  routeName?: string | null
  activeItem?: TutorialNavigationItem | null
  items: TutorialNavigationItem[]
} => {
  const currentRoute = router.currentRoute.value
  const routeName = currentRoute.name ? String(currentRoute.name) : null
  const navbarVisible = !currentRoute.meta?.hideNavbar
  const userStore = useUserStore()
  const sidebar = getSidebarItems(userStore)
  const dropdown = getDropdownItems(userStore)

  const items = [
    ...flattenNavItems(sidebar.primary, 'primary'),
    ...flattenNavItems(sidebar.secondary, 'secondary'),
    ...flattenNavItems(sidebar.admin, 'admin'),
    ...flattenNavItems(dropdown.group1, 'dropdown.group1'),
    ...flattenNavItems(dropdown.group2 ?? [], 'dropdown.group2'),
    ...flattenNavItems(dropdown.group3 ?? [], 'dropdown.group3')
  ]

  const activeItem = routeName ? (items.find(item => item.route === routeName) ?? null) : null

  return {
    navbarVisible,
    routeName,
    activeItem,
    items
  }
}

const getRect = (element: HTMLElement): TutorialElementRect => {
  const rect = element.getBoundingClientRect()
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    width: rect.width,
    height: rect.height
  }
}

const escapeForAttributeSelector = (value: string) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

const queryElementByTutorialId = (targetId: string) => {
  try {
    const escapedId = escapeForAttributeSelector(targetId)
    const exactMatch = document.querySelector<HTMLElement>(`[data-tutorial-id="${escapedId}"]`)
    if (exactMatch) return exactMatch
  } catch {
    // fall through to robust dataset scan when selector parsing fails
  }
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('[data-tutorial-id]'))
  return candidates.find(candidate => candidate.dataset.tutorialId === targetId) ?? null
}

type VisibilityRect = {
  top: number
  left: number
  right: number
  bottom: number
}

const rectFromDom = (rect: DOMRect): VisibilityRect => ({
  top: rect.top,
  left: rect.left,
  right: rect.right,
  bottom: rect.bottom
})

const rectIntersection = (first: VisibilityRect, second: VisibilityRect): VisibilityRect | null => {
  const top = Math.max(first.top, second.top)
  const left = Math.max(first.left, second.left)
  const right = Math.min(first.right, second.right)
  const bottom = Math.min(first.bottom, second.bottom)
  if (right <= left || bottom <= top) return null
  return { top, left, right, bottom }
}

const rectArea = (rect: VisibilityRect) => (rect.right - rect.left) * (rect.bottom - rect.top)

const getRenderedVisibility = (element: HTMLElement): { visible: boolean; reason?: string } => {
  if (!element.isConnected) return { visible: false, reason: 'hidden:disconnected' }
  const checkVisibility = (
    element as HTMLElement & {
      checkVisibility?: (options?: {
        visibilityProperty?: boolean
        opacityProperty?: boolean
        contentVisibilityAuto?: boolean
      }) => boolean
    }
  ).checkVisibility
  if (checkVisibility) {
    const visible = checkVisibility.call(element, {
      visibilityProperty: true,
      opacityProperty: true,
      contentVisibilityAuto: true
    })
    if (visible) return { visible: true }
  }
  const style = window.getComputedStyle(element)
  if (style.display === 'none') return { visible: false, reason: 'hidden:display' }
  if (style.visibility === 'hidden' || style.visibility === 'collapse') {
    return { visible: false, reason: 'hidden:visibility' }
  }
  if (style.opacity && Number(style.opacity) <= 0)
    return { visible: false, reason: 'hidden:opacity' }
  if (style.pointerEvents === 'none') {
    return { visible: false, reason: 'hidden:pointer-events' }
  }
  if (style.contentVisibility === 'hidden') {
    return { visible: false, reason: 'hidden:content-visibility' }
  }
  if (checkVisibility) return { visible: false, reason: 'hidden:checkVisibility' }
  return { visible: true }
}

const getClippingAncestors = (element: HTMLElement): HTMLElement[] => {
  const ancestors: HTMLElement[] = []
  let current = element.parentElement
  let depth = 0
  while (current && depth < 16) {
    const style = window.getComputedStyle(current)
    const overflow = style.overflow + style.overflowX + style.overflowY
    if (/(hidden|clip|scroll|auto)/.test(overflow)) {
      ancestors.push(current)
    }
    current = current.parentElement
    depth += 1
  }
  return ancestors
}

const getVisibleRects = (element: HTMLElement): { rects: VisibilityRect[]; reason?: string } => {
  const clientRects = Array.from(element.getClientRects())
    .map(rectFromDom)
    .filter(rect => rectArea(rect) > 0)
  if (clientRects.length === 0) return { rects: [], reason: 'hidden:no-rects' }

  const viewport: VisibilityRect = {
    top: 0,
    left: 0,
    right: window.innerWidth,
    bottom: window.innerHeight
  }

  const clippingRects = getClippingAncestors(element)
    .map(ancestor => rectFromDom(ancestor.getBoundingClientRect()))
    .filter(rect => rectArea(rect) > 0)

  const viewportClipped = clientRects
    .map(rect => rectIntersection(rect, viewport))
    .filter((rect): rect is VisibilityRect => Boolean(rect))
  if (viewportClipped.length === 0) return { rects: [], reason: 'hidden:offscreen' }

  const clipped = viewportClipped
    .map(rect => {
      return clippingRects.reduce<VisibilityRect | null>(
        (current, clip) => (current ? rectIntersection(current, clip) : null),
        rect
      )
    })
    .filter((rect): rect is VisibilityRect => Boolean(rect))
    .filter(rect => rectArea(rect) > 0)
  if (clipped.length === 0) return { rects: [], reason: 'hidden:clipped' }
  return { rects: clipped }
}

const hasVisiblePoint = (
  element: HTMLElement,
  rects: VisibilityRect[]
): { visible: boolean; reason?: string } => {
  if (rects.length === 0) return { visible: false, reason: 'hidden:no-rects' }
  const checkPoint = (x: number, y: number) => {
    const stack = document.elementsFromPoint(x, y)
    return stack.some(candidate => candidate === element || element.contains(candidate))
  }
  for (const rect of rects) {
    const points = [
      { x: rect.left + (rect.right - rect.left) / 2, y: rect.top + (rect.bottom - rect.top) / 2 },
      { x: rect.left + 1, y: rect.top + 1 },
      { x: rect.right - 1, y: rect.top + 1 },
      { x: rect.left + 1, y: rect.bottom - 1 },
      { x: rect.right - 1, y: rect.bottom - 1 }
    ]
    for (const point of points) {
      if (
        point.x < 0 ||
        point.y < 0 ||
        point.x > window.innerWidth ||
        point.y > window.innerHeight
      ) {
        continue
      }
      if (checkPoint(point.x, point.y)) return { visible: true }
    }
  }
  return { visible: false, reason: 'hidden:occluded' }
}

const getVisibility = (element: HTMLElement): { visible: boolean; reason?: string } => {
  const rendered = getRenderedVisibility(element)
  if (!rendered.visible) return rendered
  const rectsResult = getVisibleRects(element)
  if (rectsResult.rects.length === 0) {
    return { visible: false, reason: rectsResult.reason ?? 'hidden:no-rects' }
  }
  return hasVisiblePoint(element, rectsResult.rects)
}

export const registerTutorialElement = (
  element: HTMLElement,
  payload: Omit<TutorialRegistryEntry, 'element'>
) => {
  element.setAttribute('data-tutorial-id', payload.id)
  registry.set(payload.id, { ...payload, element })
}

export const unregisterTutorialElement = (id: string) => {
  registry.delete(id)
}

const createSnapshotEntry = (
  element: HTMLElement,
  payload: {
    id: string
    label: string
    role?: string | null
    priority?: number | null
    state?: Record<string, string | number | boolean | null>
  },
  route: string
): TutorialElementSnapshot => {
  const visibility = getVisibility(element)
  return {
    id: payload.id,
    label: payload.label,
    role: payload.role ?? null,
    priority: payload.priority ?? null,
    route,
    rect: visibility.visible ? getRect(element) : null,
    visible: visibility.visible,
    visibilityReason: visibility.visible ? null : (visibility.reason ?? 'hidden:unknown'),
    state: payload.state ?? undefined
  }
}

const buildNavigationCapabilities = (
  navigation: {
    items: TutorialNavigationItem[]
  },
  visibleNavItemIds: Set<string>
): TutorialCapability[] => {
  return navigation.items
    .filter(item => Boolean(item.route) || visibleNavItemIds.has(item.id))
    .map(item => ({
      id: `cap.nav.${item.id}`,
      intent: `navigate_${slugify(item.label || item.id)}`,
      label: `Navigate to ${item.label}`,
      actionType: 'navigate' as const,
      source: 'navigation' as const,
      targetId: item.id,
      route: item.route ?? null,
      priority: visibleNavItemIds.has(item.id) ? 3 : 2,
      meta: {
        group: item.group,
        visibility: visibleNavItemIds.has(item.id) ? 'visible' : 'not_visible'
      }
    }))
}

const buildElementCapabilities = (
  limitedElements: Array<{ element: HTMLElement; snapshot: TutorialElementSnapshot }>
): TutorialCapability[] => {
  const isInteractiveRole = (role?: string | null) => {
    if (!role) return false
    return (
      role === 'button' ||
      role === 'link' ||
      role === 'tab' ||
      role === 'menuitem' ||
      role === 'option'
    )
  }

  const capabilities = limitedElements
    .map(entry => entry.snapshot)
    .filter(snapshot => snapshot.visible)
    .map(snapshot => {
      const actionType: TutorialActionType = isInteractiveRole(snapshot.role)
        ? 'click'
        : 'highlight'
      return {
        id: `cap.element.${actionType}.${snapshot.id}`,
        intent: `${actionType}_${slugify(snapshot.label || snapshot.id)}`,
        label: snapshot.label,
        actionType,
        source: 'element' as const,
        targetId: snapshot.id,
        priority: snapshot.priority ?? (actionType === 'click' ? 2 : 1),
        meta: {
          role: snapshot.role ?? 'unknown'
        }
      }
    })
  return capabilities.slice(0, 80)
}

const buildContainers = (
  route: string,
  limitedElements: Array<{ element: HTMLElement; snapshot: TutorialElementSnapshot }>,
  activeDialog: HTMLElement | null
): TutorialContainerSnapshot[] => {
  const containers: TutorialContainerSnapshot[] = []
  containers.push({
    id: `container.page.${slugify(route) || 'root'}`,
    label: document.title || 'Page',
    kind: 'page',
    elementIds: limitedElements.map(entry => entry.snapshot.id).slice(0, 120)
  })

  if (activeDialog) {
    const dialogElementIds = limitedElements
      .filter(entry => activeDialog.contains(entry.element))
      .map(entry => entry.snapshot.id)
      .slice(0, 80)

    const dialogLabel =
      activeDialog
        .querySelector<HTMLElement>(
          '[data-slot="dialog-title"], [data-slot="sheet-title"], h1, h2, h3'
        )
        ?.textContent?.trim() ?? 'Active dialog'
    const isSheet = activeDialog.matches('[data-slot="sheet-content"]')

    containers.push({
      id: `container.${isSheet ? 'sheet' : 'dialog'}.${slugify(dialogLabel || 'active')}`,
      label: dialogLabel || 'Active dialog',
      kind: isSheet ? 'sheet' : 'dialog',
      elementIds: dialogElementIds
    })
  }

  const regionContainers = limitedElements
    .filter(entry => entry.snapshot.visible && entry.snapshot.role === 'region')
    .slice(0, 10)
    .map(entry => {
      const childElementIds = limitedElements
        .filter(
          candidate =>
            candidate.snapshot.id !== entry.snapshot.id && entry.element.contains(candidate.element)
        )
        .map(candidate => candidate.snapshot.id)
        .slice(0, 40)
      return {
        id: `container.region.${entry.snapshot.id}`,
        label: entry.snapshot.label,
        kind: 'region' as const,
        elementIds: childElementIds
      }
    })

  containers.push(...regionContainers)
  return containers
}

export const buildTutorialSnapshot = (route: string): TutorialUiSnapshot => {
  const snapshotStart = performance.now()
  const navigation = buildNavigationContext()
  const handlers = getSnapshotHandlers()
  const supportedActions = getSupportedActions(handlers)
  const elementsWithRefs: Array<{
    element: HTMLElement
    snapshot: TutorialElementSnapshot
    tagSource: 'registry' | 'manual-dom' | 'auto'
  }> = Array.from(registry.values()).map(entry => ({
    element: entry.element,
    tagSource: 'registry',
    snapshot: createSnapshotEntry(
      entry.element,
      {
        id: entry.id,
        label: entry.label,
        role: entry.role,
        priority: entry.priority,
        state: entry.state
      },
      route
    )
  }))

  /**
   * TODO: Auto-discovery for quick UI snapshot coverage while wiring tutorial tags.
   * Remove once explicit registrations are complete.
   */
  const autoElements = document.querySelectorAll<HTMLElement>(
    '[data-tutorial-id][data-tutorial-label]'
  )
  autoElements.forEach(element => {
    const id = element.dataset.tutorialId
    const label = element.dataset.tutorialLabel
    if (!id || !label || registry.has(id)) return
    const role = element.dataset.tutorialRole ?? null
    const priority = element.dataset.tutorialPriority
      ? Number(element.dataset.tutorialPriority)
      : null
    elementsWithRefs.push({
      element,
      tagSource: element.dataset.tutorialAutoSource === 'auto' ? 'auto' : 'manual-dom',
      snapshot: createSnapshotEntry(
        element,
        {
          id,
          label,
          role,
          priority
        },
        route
      )
    })
  })

  const filteredElements = elementsWithRefs
    .filter(entry => entry.snapshot.visible || entry.snapshot.priority !== null)
    .sort((first, second) => {
      const firstPriority = first.snapshot.priority ?? 0
      const secondPriority = second.snapshot.priority ?? 0
      if (firstPriority !== secondPriority) return secondPriority - firstPriority
      if (first.snapshot.visible !== second.snapshot.visible) {
        return first.snapshot.visible ? -1 : 1
      }
      return 0
    })

  const dialogRoots = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-slot="dialog-content"], [data-slot="sheet-content"]'
    )
  )
  const activeDialog = dialogRoots.length > 0 ? dialogRoots[dialogRoots.length - 1] : null
  const scopedElements = activeDialog
    ? filteredElements.filter(entry => activeDialog.contains(entry.element))
    : filteredElements

  const limitedElements = scopedElements.slice(0, 120)

  const activeElement = (() => {
    const element = document.activeElement
    if (!(element instanceof HTMLElement)) return null
    const id = element.dataset.tutorialId
    const label = element.dataset.tutorialLabel
    if (!id || !label) return null
    return { id, label }
  })()

  const selection = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[aria-selected="true"], [data-state="active"], [aria-current="page"]'
    )
  )
    .map(el => el.dataset.tutorialLabel)
    .filter((label): label is string => Boolean(label))
    .slice(0, 25)

  const selectedTabs = Array.from(document.querySelectorAll<HTMLElement>('[role="tab"]'))
    .filter(el => el.getAttribute('aria-selected') === 'true' || el.dataset.state === 'active')
    .map(el => ({
      id: el.dataset.tutorialId ?? '',
      label: el.dataset.tutorialLabel ?? el.textContent?.trim() ?? ''
    }))
    .filter(item => item.id.length > 0 || item.label.length > 0)
    .slice(0, 10)

  const dialogTitles = dialogRoots
    .map(dialog => {
      const title =
        dialog.querySelector<HTMLElement>('[data-slot="dialog-title"]') ||
        dialog.querySelector<HTMLElement>('[data-slot="sheet-title"]') ||
        dialog.querySelector<HTMLElement>('h1, h2, h3')
      return title?.textContent?.trim() ?? ''
    })
    .filter(title => title.length > 0)
    .slice(0, 5)

  const formInputs = Array.from(
    document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input, textarea, select'
    )
  ).filter(input => !input.disabled)

  const inputsFilled = formInputs.filter(input => {
    if (input instanceof HTMLSelectElement) {
      return input.selectedIndex > 0
    }
    if (
      input instanceof HTMLInputElement &&
      (input.type === 'checkbox' || input.type === 'radio')
    ) {
      return input.checked
    }
    return input.value.trim().length > 0
  }).length

  const primaryActions = limitedElements
    .map(entry => entry.snapshot)
    .filter(element => element.visible)
    .filter(element => element.role === 'button' || element.role === 'link')
    .map(element => element.label)
    .filter(label => label.length > 0)
    .slice(0, 12)

  const visibleNavItems = navigation.items
    .map(item => {
      const element = queryElementByTutorialId(item.id)
      if (!element) return null
      const visibility = getVisibility(element)
      if (!visibility.visible) return null
      return { id: item.id, label: item.label }
    })
    .filter((item): item is { id: string; label: string } => Boolean(item))
  const visibleNavItemIds = new Set(visibleNavItems.map(item => item.id))

  const primaryCta = (() => {
    const ctaCandidates = limitedElements
      .map(entry => entry.snapshot)
      .filter(element => element.visible && (element.role === 'button' || element.role === 'link'))
    const preferred = ctaCandidates.find(
      element =>
        element.label.toLowerCase().includes('generate') ||
        element.id.toLowerCase().includes('generate')
    )
    const pick = preferred ?? ctaCandidates[0]
    if (!pick) return null
    return { id: pick.id, label: pick.label }
  })()

  const visibleElements = elementsWithRefs.filter(element => element.snapshot.visible)
  const hiddenElements = elementsWithRefs.filter(element => !element.snapshot.visible)
  const hiddenReasonCountsMap = hiddenElements.reduce<Map<string, number>>((acc, entry) => {
    const reason = entry.snapshot.visibilityReason ?? 'hidden:unknown'
    acc.set(reason, (acc.get(reason) ?? 0) + 1)
    return acc
  }, new Map())
  const hiddenReasonCounts = Array.from(hiddenReasonCountsMap.entries())
    .map(([reason, count]) => ({ reason, count }))
    .sort((first, second) => second.count - first.count)
  const idCounts = elementsWithRefs.reduce<Map<string, number>>((acc, entry) => {
    acc.set(entry.snapshot.id, (acc.get(entry.snapshot.id) ?? 0) + 1)
    return acc
  }, new Map())
  const idCollisions = Array.from(idCounts.values()).reduce((sum, count) => {
    if (count <= 1) return sum
    return sum + (count - 1)
  }, 0)
  const aliasMetrics = getTutorialAliasMetrics()
  const resolutionMetrics = getTutorialActionResolutionMetrics()
  const totalRegisteredTagged = elementsWithRefs.filter(
    entry => entry.tagSource === 'registry'
  ).length
  const totalManualDomTagged = elementsWithRefs.filter(
    entry => entry.tagSource === 'manual-dom'
  ).length
  const totalAutoTagged = elementsWithRefs.filter(entry => entry.tagSource === 'auto').length
  const totalManualTagged = totalRegisteredTagged + totalManualDomTagged
  const totalTagged = elementsWithRefs.length
  const autoTagRate = totalTagged > 0 ? Number((totalAutoTagged / totalTagged).toFixed(4)) : null
  const manualTagRate =
    totalTagged > 0 ? Number((totalManualTagged / totalTagged).toFixed(4)) : null

  const capabilities = uniqueCapabilities([
    ...buildNavigationCapabilities(navigation, visibleNavItemIds),
    ...buildElementCapabilities(limitedElements),
    ...buildHandlerCapabilities(handlers)
  ])

  const containers = buildContainers(route, limitedElements, activeDialog)
  const snapshotBuildMs = Number((performance.now() - snapshotStart).toFixed(2))

  return {
    route,
    pageTitle: document.title,
    timestamp: new Date().toISOString(),
    supportedActions,
    capabilities,
    containers,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    openDialogs: dialogRoots.length,
    dialogTitles: dialogTitles.length ? dialogTitles : undefined,
    activeElement,
    selection: selection.length ? selection : undefined,
    selectedTabs: selectedTabs.length ? selectedTabs : undefined,
    formSummary: {
      inputsTotal: formInputs.length,
      inputsFilled
    },
    primaryActions,
    primaryCta,
    history: {
      routes: getRecentRoutes()
    },
    navigation,
    affordances: {
      visibleNavItems,
      visibleNavCount: visibleNavItems.length,
      inputsFilled,
      inputsTotal: formInputs.length,
      selectedTabsCount: selectedTabs.length,
      primaryActionsCount: primaryActions.length
    },
    diagnostics: {
      totalTagged,
      totalVisible: visibleElements.length,
      totalManualTagged,
      totalAutoTagged,
      totalRegisteredTagged,
      totalManualDomTagged,
      autoTagRate,
      manualTagRate,
      snapshotBuildMs,
      idCollisions,
      aliasHits: aliasMetrics.aliasHits,
      fallbackMatches: aliasMetrics.fallbackMatches,
      targetResolutions: resolutionMetrics.total,
      targetResolvedFirstAttempt: resolutionMetrics.firstAttempt,
      targetResolvedRecovered: resolutionMetrics.recovered,
      targetResolutionFailed: resolutionMetrics.failed,
      targetFirstAttemptRate: resolutionMetrics.firstAttemptRate,
      visibleSampleIds: visibleElements.map(element => element.snapshot.id).slice(0, 10),
      hiddenSampleIds: hiddenElements.map(element => element.snapshot.id).slice(0, 10),
      hiddenReasonCounts: hiddenReasonCounts.slice(0, 10)
    },
    elements: limitedElements.map(entry => entry.snapshot)
  }
}
